import { zodResolver } from "@hookform/resolvers/zod";
import { type ColumnDef } from "@tanstack/react-table";
import { GitBranch, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { AppShell } from "@/components/AppShell";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api";
import { useCategories, useCreateFlow, useDeleteFlow, useFlows } from "@/hooks/api/flows";
import type { Flow, FlowStatus } from "@/types/api";

const schema = z.object({
  title: z.string().min(1, "Required"),
  category_id: z.string().min(1, "Pick a category"),
});
type FormValues = z.infer<typeof schema>;

const STATUS_KIND: Record<FlowStatus, "success" | "pending" | "danger"> = {
  PUBLISHED: "success",
  DRAFT: "pending",
  ARCHIVED: "danger",
};

export default function AdminFlowsPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useFlows();
  const { data: categories } = useCategories();
  const createFlow = useCreateFlow();
  const deleteFlow = useDeleteFlow();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteFlowId, setDeleteFlowId] = useState<string | null>(null);
  const [deleteFlowTitle, setDeleteFlowTitle] = useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: "", category_id: "" },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const flow = await createFlow.mutateAsync(values);
      setDialogOpen(false);
      form.reset({ title: "", category_id: "" });
      navigate(`/flows/${flow.id}/builder`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't create that script."));
    }
  };

  const handleDelete = async () => {
    if (!deleteFlowId) return;

    try {
      await deleteFlow.mutateAsync(deleteFlowId);
      toast.success(`"${deleteFlowTitle}" deleted`);
      setDeleteFlowId(null);
      setDeleteFlowTitle("");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete that script."));
    }
  };

  const columns: ColumnDef<Flow>[] = [
    { accessorKey: "title", header: "Title" },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ row }) => row.original.category.name,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusPill kind={STATUS_KIND[row.original.status]}>{row.original.status}</StatusPill>
      ),
    },
    { accessorKey: "version", header: "Version" },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteFlowId(row.original.id);
            setDeleteFlowTitle(row.original.title);
          }}
        >
          <Trash2 className="h-4 w-4 text-danger" />
        </Button>
      ),
    },
  ];

  const categoryOptions = categories?.results ?? [];

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <h1 className="m-0 text-2xl font-bold text-slate-900">Scripts</h1>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New script
        </Button>
      </div>

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          loading={isLoading}
          error={error ? "Couldn't load scripts." : undefined}
          onRowClick={(flow) => navigate(`/flows/${flow.id}/builder`)}
          empty={
            <EmptyState
              icon={GitBranch}
              title="No scripts yet"
              description="Create your first script to start building a process for agents to follow."
              action={<Button onClick={() => setDialogOpen(true)}>New script</Button>}
            />
          }
        />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New script</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Customer disputes a charge" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categoryOptions.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {categoryOptions.length === 0 && (
                      <p className="text-xs text-slate-500">
                        No categories yet — create one on the Categories page first.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="submit"
                  loading={createFlow.isPending}
                  disabled={categoryOptions.length === 0}
                >
                  Create &amp; start writing
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFlowId} onOpenChange={(open: boolean) => !open && setDeleteFlowId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete script?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will permanently delete "{deleteFlowTitle}". This action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setDeleteFlowId(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              loading={deleteFlow.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
